import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react'

import SlideUp from 'common-components/SlideUp'

interface IMobileDrawerContextType {
  /** Mount `content` in the timeline-level drawer and slide it up. */
  openDrawer: (content: ReactNode) => void
  closeDrawer: () => void
}

const MobileDrawerContext = createContext<IMobileDrawerContextType | undefined>(
  undefined
)

/**
 * A single mobile slide-up drawer hosted by a stably-mounted parent (the
 * Timeline), with its content pushed in imperatively via context.

 * `content` is intentionally kept after `closeDrawer` so it stays rendered
 * through the exit animation; it's only swapped when the next `openDrawer`
 * runs. Because `children` keeps a stable element identity across this
 * provider's own state updates, opening/closing the drawer never re-renders the
 * timeline subtree — only context consumers (and the imperative API is stable,
 * so even those don't re-render from this).
 */
export const MobileDrawerProvider: React.FC<{ children: ReactNode }> = ({
  children
}) => {
  const [content, setContent] = useState<ReactNode>(null)
  const [isOpen, setIsOpen] = useState(false)

  const openDrawer = useCallback((node: ReactNode) => {
    setContent(node)
    setIsOpen(true)
  }, [])

  const closeDrawer = useCallback(() => {
    setIsOpen(false)
  }, [])

  const value = useMemo(
    () => ({ openDrawer, closeDrawer }),
    [openDrawer, closeDrawer]
  )

  return (
    <MobileDrawerContext.Provider value={value}>
      {children}
      <SlideUp isOpen={isOpen} onClose={closeDrawer}>
        {content}
      </SlideUp>
    </MobileDrawerContext.Provider>
  )
}

export const useMobileDrawerContext = () => {
  const context = useContext(MobileDrawerContext)

  if (!context) {
    throw new Error(
      'useMobileDrawerContext must be used within a MobileDrawerProvider'
    )
  }

  return context
}
